export interface TourRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface TourPopoverPosition {
  top: string | null;
  bottom: string | null;
  left: string;
  width: string;
}

export interface TourLayout {
  mobile: boolean;
  spot: TourRect;
  maskPath: string;
  popover: TourPopoverPosition;
}

export const TOUR_SPOT_MARGIN = 8;
export const TOUR_SPOT_RADIUS = 8;
export const TOUR_POPOVER_WIDTH = 320;
const EDGE = 12;
const GAP = 12;
const MOBILE_EDGE = 16;
export const TOUR_MOBILE_MAX_WIDTH = 767;

export const EMPTY_TOUR_LAYOUT: TourLayout = {
  mobile: false,
  spot: { top: 0, left: 0, width: 0, height: 0 },
  maskPath: '',
  popover: { top: '0px', bottom: null, left: '0px', width: `${TOUR_POPOVER_WIDTH}px` }
};

/** Recorte (com margem) ao redor do alvo; a máscara usa path evenodd — sem sombras. */
export function computeSpot(target: TourRect): TourRect {
  return {
    top: target.top - TOUR_SPOT_MARGIN,
    left: target.left - TOUR_SPOT_MARGIN,
    width: target.width + TOUR_SPOT_MARGIN * 2,
    height: target.height + TOUR_SPOT_MARGIN * 2
  };
}

export function computeMaskPath(
  viewportWidth: number,
  viewportHeight: number,
  spot: TourRect
): string {
  const r = Math.min(TOUR_SPOT_RADIUS, spot.width / 2, spot.height / 2);
  const { left: x, top: y, width: w, height: h } = spot;
  return [
    `M0 0H${viewportWidth}V${viewportHeight}H0Z`,
    `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}`,
    `A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}`,
    `V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`
  ].join('');
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

export function computeTourLayout(
  target: TourRect,
  viewport: { width: number; height: number },
  popoverHeight: number
): TourLayout {
  const spot = computeSpot(target);
  const maskPath = computeMaskPath(viewport.width, viewport.height, spot);
  const mobile = viewport.width <= TOUR_MOBILE_MAX_WIDTH;
  const spotBottom = spot.top + spot.height;
  const spotRight = spot.left + spot.width;

  if (mobile) {
    const width = viewport.width - MOBILE_EDGE * 2;
    const inUpperHalf = spot.top + spot.height / 2 < viewport.height / 2;
    const below = spotBottom + GAP;
    const above = spot.top - GAP - popoverHeight;
    let popover: TourPopoverPosition;
    if (inUpperHalf) {
      popover =
        below + popoverHeight <= viewport.height - EDGE
          ? { top: `${below}px`, bottom: null, left: `${MOBILE_EDGE}px`, width: `${width}px` }
          : {
              top: null,
              bottom: `${MOBILE_EDGE}px`,
              left: `${MOBILE_EDGE}px`,
              width: `${width}px`
            };
    } else {
      popover =
        above >= EDGE
          ? { top: `${above}px`, bottom: null, left: `${MOBILE_EDGE}px`, width: `${width}px` }
          : {
              top: `${MOBILE_EDGE}px`,
              bottom: null,
              left: `${MOBILE_EDGE}px`,
              width: `${width}px`
            };
    }
    return { mobile, spot, maskPath, popover };
  }

  const width = TOUR_POPOVER_WIDTH;
  const centeredLeft = clamp(
    spot.left + spot.width / 2 - width / 2,
    EDGE,
    viewport.width - width - EDGE
  );
  const centeredTop = clamp(
    spot.top + spot.height / 2 - popoverHeight / 2,
    EDGE,
    viewport.height - popoverHeight - EDGE
  );
  const candidates: Array<{ fits: boolean; top: number; left: number }> = [
    {
      fits: spotRight + GAP + width <= viewport.width - EDGE,
      top: centeredTop,
      left: spotRight + GAP
    },
    {
      fits: spotBottom + GAP + popoverHeight <= viewport.height - EDGE,
      top: spotBottom + GAP,
      left: centeredLeft
    },
    { fits: spot.left - GAP - width >= EDGE, top: centeredTop, left: spot.left - GAP - width },
    {
      fits: spot.top - GAP - popoverHeight >= EDGE,
      top: spot.top - GAP - popoverHeight,
      left: centeredLeft
    }
  ];
  const chosen =
    candidates.find((candidate) => candidate.fits) ??
    ({
      top: clamp(spotBottom + GAP, EDGE, viewport.height - popoverHeight - EDGE),
      left: centeredLeft
    } as const);
  return {
    mobile,
    spot,
    maskPath,
    popover: { top: `${chosen.top}px`, bottom: null, left: `${chosen.left}px`, width: `${width}px` }
  };
}
